import BillingIcon from '@atlaskit/icon/glyph/billing';
import DocumentsIcon from '@atlaskit/icon/glyph/document';
import PersonIcon from '@atlaskit/icon/glyph/person-circle';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import { AtlassianIcon } from '@atlaskit/logo';
import { colors } from '@atlaskit/theme';
import { getATLContextUrl } from '@atlassian/atl-context';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { LinkConfiguration, LinkConfigurationMap } from '../../../clients/sites/model';
import { LINK_URLS } from '../../../constants/links';
import { createIcon } from '../ProductCard/ProductIcon';

// At the time of making this, the Atlassian Icon's gradient portion
// has trouble updating up "currentColor" on :hover, it seems.
// This results in a logo that is half colored / half grey when you hover over it.
// This component forces a solid color.
const AtlassianIconSolidColor = (styled(AtlassianIcon)`
  svg > g path {
    fill: currentColor;
  }
` as unknown) as typeof AtlassianIcon;

interface QuestionIconProps {
  className: string;
  label: string;
}

const QuestionCircleComponent: React.FC<QuestionIconProps> = ({ className, ...props }) => (
  <span className={className}>
    <QuestionCircleIcon {...props} />
  </span>
);

const QuestionCircleIconStyled = (styled(QuestionCircleComponent)`
  &:hover,
  svg {
    & > g {
      circle:nth-child(2) {
        fill: ${colors.N700} !important;
      }
      path {
        fill: ${colors.N700} !important;
      }
    }
  }
` as unknown) as typeof QuestionCircleIcon;

const messages = defineMessages({
  admin: {
    defaultMessage: 'Administration',
    description: 'Link to the administration portal or site admin',
    id: 'uchi.admin',
  },
  atlassianCommunity: {
    defaultMessage: 'Atlassian Community',
    description: 'Link to Atlassian Community portal',
    id: 'uchi.atlassianCommunity',
  },
  atlassianDocumentation: {
    defaultMessage: 'Atlassian Documentation',
    description: 'Link to Atlassian Documentation portal',
    id: 'uchi.atlassianDocumentation',
  },
  atlassianLicensing: {
    defaultMessage: 'Self-managed licensing',
    description: 'Link to Atlassian Licensing portal',
    id: 'uchi.atlassianLicensing',
  },
  atlassianProfile: {
    defaultMessage: 'Account settings',
    description: 'Link to manage profile',
    id: 'uchi.atlassianProfile',
  },
  atlassianSupport: {
    defaultMessage: 'Atlassian Support',
    description: 'Link to Atlassian Support portal',
    id: 'uchi.atlassianSupport',
  },
  atlassianWebsite: {
    defaultMessage: 'Atlassian.com',
    description: 'Link to Atlassian website',
    id: 'uchi.atlassianWebsite',
  },
});

type AvailableLinkType =
  | 'ADMINISTRATION'
  | 'ACCOUNT_SETTINGS'
  | 'ATLASSIAN_SUPPORT'
  // | 'ATLASSIAN_COMMUNITY'
  | 'ATLASSIAN_DOCUMENTATION'
  | 'ATLASSIAN_WEBSITE'
  | 'ATLASSIAN_LICENSING'
  | string;

const AVAILABLE_LINKS: AvailableLinkType[] = [
  'ADMINISTRATION',
  'ACCOUNT_SETTINGS',
  'ATLASSIAN_SUPPORT',
  // 'ATLASSIAN_COMMUNITY',
  'ATLASSIAN_LICENSING',
  'ATLASSIAN_DOCUMENTATION',
  'ATLASSIAN_WEBSITE',
];

export const StaticLinkConfig: LinkConfigurationMap = {
  ADMINISTRATION: {
    Icon: createIcon(SettingsIcon),
    label: <FormattedMessage {...messages.admin} />,
    description: 'Administration',
    labelAnalyticKey: 'Administration',
    href: LINK_URLS.ADMIN_ATLASSIAN,
    synchroniseSession: true,
    synchronisationApplication: 'admin',
  },
  ACCOUNT_SETTINGS: {
    Icon: createIcon(PersonIcon),
    label: <FormattedMessage {...messages.atlassianProfile} />,
    description: 'Account settings',
    labelAnalyticKey: 'Account settings',
    href: LINK_URLS.MANAGE_PROFILE,
    synchroniseSession: true,
  },
  ATLASSIAN_SUPPORT: {
    Icon: createIcon(QuestionCircleIconStyled),
    label: <FormattedMessage {...messages.atlassianSupport} />,
    description: 'Atlassian Support',
    labelAnalyticKey: 'Atlassian Support',
    href: LINK_URLS.SUPPORT,
  },
  // No FedRamp equivalent
  // ATLASSIAN_COMMUNITY: {
  //   Icon: createIcon(PeopleGroupIcon),
  //   label: <FormattedMessage {...messages.atlassianCommunity} />,
  //   description: 'Atlassian Community',
  //   labelAnalyticKey: 'Atlassian Community',
  //   href: getATLContextUrl('community'),
  // },
  ATLASSIAN_LICENSING: {
    Icon: createIcon(BillingIcon),
    label: <FormattedMessage {...messages.atlassianLicensing} />,
    description: 'Manage product licenses',
    labelAnalyticKey: 'Manage product licenses',
    href: LINK_URLS.LICENSING,
    synchroniseSession: true,
    synchronisationApplication: 'mac',
  },
  ATLASSIAN_DOCUMENTATION: {
    Icon: createIcon(DocumentsIcon),
    label: <FormattedMessage {...messages.atlassianDocumentation} />,
    description: 'Atlassian Documentation',
    labelAnalyticKey: 'Atlassian Documentation',
    href: getATLContextUrl('confluence'),
  },
  ATLASSIAN_WEBSITE: {
    Icon: createIcon(AtlassianIconSolidColor),
    label: <FormattedMessage {...messages.atlassianWebsite} />,
    description: 'Atlassian Website',
    labelAnalyticKey: 'Atlassian Website',
    href: LINK_URLS.WAC,
    synchroniseSession: true,
    synchronisationApplication: 'wac',
  },
};

export const getHelpfulLinks = (linkConfiguration: LinkConfigurationMap, withAdmin?: boolean): LinkConfiguration[] =>
  AVAILABLE_LINKS.reduce<LinkConfiguration[]>((acc, linkKey) => {
    if (withAdmin || linkKey !== 'ADMINISTRATION') {
      acc.push(linkConfiguration[linkKey]);
    }
    return acc;
  }, []);

export const getSiteAdminLink = (siteUrl: string, linkConfiguration: LinkConfigurationMap): LinkConfiguration => ({
  ...linkConfiguration.ADMINISTRATION,
  href: `${siteUrl}/admin`,
});
